/**
 * BrandContextForm component - Form for managing project brand context
 * Handles logo upload, colors, distinctive assets, and product names
 */

import { Upload, X, Plus, Loader2 } from 'lucide-react';
import { useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import type { BrandContext } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export interface BrandContextFormProps {
  brandContext?: BrandContext;
  onChange: (brandContext: BrandContext) => void;
  onUploadLogo?: (file: File) => Promise<string>;
  isLoading?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Validate hex color format
 */
function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

// ============================================================================
// COMPONENT
// ============================================================================

export function BrandContextForm({
  brandContext = {},
  onChange,
  onUploadLogo,
  isLoading = false,
}: BrandContextFormProps) {
  const [logoUploading, setLogoUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [colorInput, setColorInput] = useState('');
  const [colorError, setColorError] = useState('');
  const [assetInput, setAssetInput] = useState('');
  const [productInput, setProductInput] = useState('');

  // ============================================================================
  // LOGO UPLOAD HANDLERS
  // ============================================================================

  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processLogoFile(file);
    }
  };

  const handleRemoveLogo = () => {
    onChange({ ...brandContext, logoUrl: undefined });
  };

  const processLogoFile = async (file: File) => {
    if (!onUploadLogo) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }

    try {
      setLogoUploading(true);
      const logoUrl = await onUploadLogo(file);
      onChange({ ...brandContext, logoUrl });
    } catch (error) {
      console.error('Failed to upload logo:', error);
      alert('Failed to upload logo. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isLoading || logoUploading) return;

    const files = Array.from(e.dataTransfer.files);
    const file = files[0];
    if (file) {
      processLogoFile(file);
    }
  };

  // ============================================================================
  // COLOR HANDLERS
  // ============================================================================

  const handleAddColor = () => {
    const trimmedColor = colorInput.trim();

    if (!trimmedColor) {
      setColorError('Please enter a color');
      return;
    }

    if (!isValidHexColor(trimmedColor)) {
      setColorError('Please enter a valid hex color (e.g., #FF5733)');
      return;
    }

    const currentColors = brandContext.primaryColors || [];
    if (currentColors.includes(trimmedColor)) {
      setColorError('This color has already been added');
      return;
    }

    onChange({
      ...brandContext,
      primaryColors: [...currentColors, trimmedColor],
    });
    setColorInput('');
    setColorError('');
  };

  const handleRemoveColor = (colorToRemove: string) => {
    const currentColors = brandContext.primaryColors || [];
    onChange({
      ...brandContext,
      primaryColors: currentColors.filter((color) => color !== colorToRemove),
    });
  };

  // ============================================================================
  // DISTINCTIVE ASSETS HANDLERS
  // ============================================================================

  const handleAddAsset = () => {
    const trimmedAsset = assetInput.trim();

    if (!trimmedAsset) return;

    const currentAssets = brandContext.distinctiveAssets || [];
    if (currentAssets.includes(trimmedAsset)) return;

    onChange({
      ...brandContext,
      distinctiveAssets: [...currentAssets, trimmedAsset],
    });
    setAssetInput('');
  };

  const handleRemoveAsset = (assetToRemove: string) => {
    const currentAssets = brandContext.distinctiveAssets || [];
    onChange({
      ...brandContext,
      distinctiveAssets: currentAssets.filter((asset) => asset !== assetToRemove),
    });
  };

  // ============================================================================
  // PRODUCT NAMES HANDLERS
  // ============================================================================

  const handleAddProduct = () => {
    const trimmedProduct = productInput.trim();

    if (!trimmedProduct) return;

    const currentProducts = brandContext.productNames || [];
    if (currentProducts.includes(trimmedProduct)) return;

    onChange({
      ...brandContext,
      productNames: [...currentProducts, trimmedProduct],
    });
    setProductInput('');
  };

  const handleRemoveProduct = (productToRemove: string) => {
    const currentProducts = brandContext.productNames || [];
    onChange({
      ...brandContext,
      productNames: currentProducts.filter((product) => product !== productToRemove),
    });
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="space-y-8">
      {/* Logo Upload Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-primary mb-1">Brand Logo</h3>
          <p className="text-xs text-secondary">
            Upload your brand logo to help AI identify branding in creatives
          </p>
        </div>

        {brandContext.logoUrl ? (
          <div className="relative inline-block">
            <img
              src={brandContext.logoUrl}
              alt="Brand logo"
              className="h-24 w-auto rounded-lg border border-border-subtle shadow-sm"
            />
            <button
              onClick={handleRemoveLogo}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-danger text-inverse hover:bg-danger/90 transition-colors"
              aria-label="Remove logo"
              disabled={isLoading}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="file"
              id="logo-upload"
              accept="image/*"
              onChange={handleLogoUpload}
              className="sr-only"
              disabled={isLoading || logoUploading}
            />
            <label
              htmlFor="logo-upload"
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={cn(
                'flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
                isDragging
                  ? 'border-brand bg-brand/10'
                  : 'border-border-default hover:border-brand hover:bg-brand/5',
                (isLoading || logoUploading) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {logoUploading ? (
                <Loader2 className="h-8 w-8 text-muted animate-spin mb-2" />
              ) : (
                <Upload className="h-8 w-8 text-muted mb-2" />
              )}
              <span className="text-sm font-medium text-secondary">
                {logoUploading ? 'Uploading...' : 'Click to upload or drag and drop'}
              </span>
              <span className="text-xs text-muted mt-1">
                PNG, JPG, SVG up to 5MB
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Primary Colors Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-primary mb-1">Primary Colors</h3>
          <p className="text-xs text-secondary">
            Add your brand&apos;s primary colors as hex codes
          </p>
        </div>

        <div className="flex gap-2">
          <Input
            value={colorInput}
            onChange={(e) => {
              setColorInput(e.target.value);
              setColorError('');
            }}
            placeholder="#FF5733"
            error={!!colorError}
            errorMessage={colorError}
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddColor();
              }
            }}
            className="flex-1"
          />
          <Button
            onClick={handleAddColor}
            variant="secondary"
            leftIcon={<Plus className="h-4 w-4" />}
            disabled={isLoading}
          >
            Add
          </Button>
        </div>

        {brandContext.primaryColors && brandContext.primaryColors.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {brandContext.primaryColors.map((color) => (
              <div
                key={color}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border-subtle bg-surface-subtle"
              >
                <div
                  className="w-5 h-5 rounded border border-border-default"
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm font-mono text-secondary">{color}</span>
                <button
                  onClick={() => handleRemoveColor(color)}
                  className="text-muted hover:text-danger transition-colors"
                  disabled={isLoading}
                  aria-label={`Remove ${color}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Distinctive Assets Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-primary mb-1">Distinctive Assets</h3>
          <p className="text-xs text-secondary">
            Visual elements unique to your brand (e.g., mascot, logo shape, patterns)
          </p>
        </div>

        <div className="flex gap-2">
          <Input
            value={assetInput}
            onChange={(e) => setAssetInput(e.target.value)}
            placeholder="e.g., Red swoosh, Golden arches"
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddAsset();
              }
            }}
            className="flex-1"
          />
          <Button
            onClick={handleAddAsset}
            variant="secondary"
            leftIcon={<Plus className="h-4 w-4" />}
            disabled={isLoading}
          >
            Add
          </Button>
        </div>

        {brandContext.distinctiveAssets && brandContext.distinctiveAssets.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {brandContext.distinctiveAssets.map((asset) => (
              <div
                key={asset}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-pill border border-border-subtle bg-surface-subtle text-sm text-secondary"
              >
                {asset}
                <button
                  onClick={() => handleRemoveAsset(asset)}
                  className="text-muted hover:text-danger transition-colors"
                  disabled={isLoading}
                  aria-label={`Remove ${asset}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product Names Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-primary mb-1">Product Names</h3>
          <p className="text-xs text-secondary">
            Your product or service names to track mentions
          </p>
        </div>

        <div className="flex gap-2">
          <Input
            value={productInput}
            onChange={(e) => setProductInput(e.target.value)}
            placeholder="e.g., Product X, Service Y"
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddProduct();
              }
            }}
            className="flex-1"
          />
          <Button
            onClick={handleAddProduct}
            variant="secondary"
            leftIcon={<Plus className="h-4 w-4" />}
            disabled={isLoading}
          >
            Add
          </Button>
        </div>

        {brandContext.productNames && brandContext.productNames.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {brandContext.productNames.map((product) => (
              <div
                key={product}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-pill border border-border-subtle bg-surface-subtle text-sm text-secondary"
              >
                {product}
                <button
                  onClick={() => handleRemoveProduct(product)}
                  className="text-muted hover:text-danger transition-colors"
                  disabled={isLoading}
                  aria-label={`Remove ${product}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
